export const acceptImagesFileType = {
  description: "Images",
  accept: {
    "image/avif": [".avif", ".avifs"],
    "image/jpeg": [".jpeg", ".jpg"],
    "image/png": [".png"],
    "image/gif": [".gif"],
    "image/webp": [".webp"],
  },
} as const satisfies FilePickerAcceptType

export const acceptStillImagesFileType = {
  description: "Still Images",
  accept: {
    "image/jpeg": [".jpeg", ".jpg"],
    "image/png": [".png"],
    "image/webp": [".webp"],
  },
} as const satisfies FilePickerAcceptType

export const acceptVideosFileType = {
  description: "Videos",
  accept: {
    "video/mp4": [".m4v", ".mp4", ".mp4v", ".mpg4"],
    "video/quicktime": [".mov"],
  },
} as const satisfies FilePickerAcceptType

export const acceptAudioFileType = {
  description: "Audio",
  accept: {
    "audio/mpeg": [".mp3"],
    "audio/wav": [".wav"],
    "audio/ogg": [".ogg"],
    "audio/aac": [".aac"],
  },
} as const satisfies FilePickerAcceptType

export const acceptModelsFileType = {
  description: "3D Models",
  accept: {
    "model/gltf+json": [".gltf"],
    "model/gltf-binary": [".glb"],
  },
} as const satisfies FilePickerAcceptType

export const acceptCompressedFileType = {
  description: "Compressed Files",
  accept: {
    "application/zip": [".zip"],
    "application/x-zip-compressed": [".zip"],
  },
} as const satisfies FilePickerAcceptType

export const acceptImagesVideosFileType = {
  description: "Images or Videos",
  accept: {
    ...acceptImagesFileType.accept,
    ...acceptVideosFileType.accept,
  },
} as const satisfies FilePickerAcceptType

export const acceptImagesVideosModelsFileType = {
  description: "Images, Videos or 3D Models",
  accept: {
    ...acceptImagesVideosFileType.accept,
    ...acceptModelsFileType.accept,
  },
} as const satisfies FilePickerAcceptType

function isValidType(file: File, type: FilePickerAcceptType): boolean {
  for (const contentType in type.accept) {
    if (file.type === contentType) {
      return true
    }

    for (const fileExtension of type.accept[contentType]) {
      if (file.name.toLowerCase().endsWith(fileExtension)) {
        return true
      }
    }
  }

  return false
}

/** Returns whether the content type of the given File exists in the given types. */
export const validateFileType = (
  file: File,
  types: FilePickerAcceptType | FilePickerAcceptType[],
): boolean => {
  if (!Array.isArray(types)) {
    return isValidType(file, types)
  }

  for (const type of types) {
    if (isValidType(file, type)) {
      return true
    }
  }

  return false
}

export const isModelMimeType = (mimeType: string): boolean => {
  return mimeType in acceptModelsFileType.accept
}

export const isVideoMimeType = (mimeType: string): boolean => {
  return mimeType in acceptVideosFileType.accept
}

export const isImageMimeType = (mimeType: string): boolean => {
  return mimeType in acceptImagesFileType.accept
}

export type FileRejection = {
  file: File
  reason:
    | { code: "MAX_SIZE_EXCEEDED"; maxSize: number }
    | { code: "INVALID_TYPE"; acceptType: FilePickerAcceptType }
}

export type FileDimensions = {
  width: number
  height: number
  type: "ratio" | "pixel"
}
